/**
 * Client-side transcript export projection for the Advanced Export modal's LIVE
 * PREVIEW (and the plain-text we hand to the clipboard fallback).
 *
 * IMPORTANT: this is a *non-authoritative projection*. The downloaded artifacts
 * (txt/pdf/docx/srt) are always generated by the backend so there is a single
 * source of truth. This file exists only so the preview can update instantly
 * without a round-trip. It mirrors, byte-for-byte, the backend semantics in:
 *   audioscribe-api/src/audio-file/service/export-model.builder.ts
 *   audioscribe-api/src/audio-file/service/export-format.util.ts
 *   audioscribe-api/src/audio-file/audio-file.service.ts (parseTranscriptJson)
 *
 * The shared contract fixtures (src/utils/__fixtures__/export-contract.json,
 * kept identical to the backend copy) pin the two together. If you change
 * formatting here, change the backend too — and vice versa.
 */

export const BRANDING_FOOTER =
  "Exported by AudioScribe · https://audioscribe.io";

/** Group a flat words[] (from transcriptJson.words) into speaker sections. */
export function groupWordsIntoSections(words) {
  if (!Array.isArray(words) || words.length === 0) return [];

  const sections = [];
  let currentSpeaker = "";
  let sectionWords = [];
  let sectionStartTime = null;

  words.forEach((wordData) => {
    if (!wordData || !wordData.text) return;
    const speaker = wordData.speaker ?? "Unknown";
    const start = wordData.start ?? null;

    if (currentSpeaker !== speaker) {
      if (currentSpeaker !== "") {
        sections.push({
          speaker: currentSpeaker,
          startTime: sectionStartTime,
          words: sectionWords,
        });
      }
      currentSpeaker = speaker;
      sectionWords = [];
      sectionStartTime = start !== null ? Number(start) : null;
    }
    sectionWords.push(wordData.text);
  });

  if (sectionWords.length > 0) {
    sections.push({
      speaker: currentSpeaker,
      startTime: sectionStartTime,
      words: sectionWords,
    });
  }

  return sections;
}

/** Build the export model (IR) from sections + options. */
export function buildExportModel(sections, options) {
  const grouped = groupSections(sections, options);

  const blocks = grouped.map((section) => ({
    speaker: options.showSpeakerNames ? section.speaker : null,
    startMs: options.showTimestamps ? section.startTime : null,
    text: section.words.join(" "),
  }));

  return { blocks, branding: !options.removeBranding };
}

function groupSections(sections, options) {
  if (sections.length === 0) return [];

  if (options.combineAllParagraphs) {
    return [
      {
        speaker: null,
        startTime: sections[0].startTime,
        words: sections.flatMap((s) => s.words),
      },
    ];
  }

  if (options.combineSpeakerParagraphs) {
    const merged = [];
    for (const section of sections) {
      const last = merged[merged.length - 1];
      if (last && last.speaker === section.speaker) {
        last.words = last.words.concat(section.words);
      } else {
        merged.push({
          speaker: section.speaker,
          startTime: section.startTime,
          words: [...section.words],
        });
      }
    }
    return merged;
  }

  return sections;
}

/** Format milliseconds as HH:MM:SS. */
export function formatTimestamp(milliseconds) {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Format a speaker label: default single-uppercase-letter diarization labels
 * ("A", "B", …) render as "Speaker A"; user-assigned names show verbatim. Mirrors
 * the in-app transcript viewer (`getSpeakerDisplayName`) and the backend
 * `formatSpeakerLabel`, so the preview matches both the screen and the download.
 * Returns "" for an absent or blank/whitespace-only label (callers treat falsy
 * as "no speaker to show").
 */
export function formatSpeakerLabel(speaker) {
  const value = (speaker ?? "").trim();
  return /^[A-Z]$/.test(value) ? `Speaker ${value}` : value;
}

/** The header line for a block, or null when neither speaker nor timestamp shows. */
export function formatBlockHeader(block) {
  const hasTime = block.startMs !== null && block.startMs !== undefined;
  const label = formatSpeakerLabel(block.speaker);

  if (label && hasTime) {
    return `${label} [${formatTimestamp(block.startMs)}]:`;
  }
  if (label) return `${label}:`;
  if (hasTime) return `[${formatTimestamp(block.startMs)}]:`;
  return null;
}

/** Render an export model to plain text (preview + clipboard fallback). */
export function renderModelToText(model) {
  const body = model.blocks
    .map((block) => {
      const header = formatBlockHeader(block);
      return header ? `${header}\n${block.text}` : block.text;
    })
    .join("\n\n");

  if (model.branding) {
    return body ? `${body}\n\n${BRANDING_FOOTER}\n` : `${BRANDING_FOOTER}\n`;
  }
  return body;
}

/** Convenience: words[] + options -> plain text, matching the backend txt export. */
export function renderTranscriptText(words, options) {
  const sections = groupWordsIntoSections(words);
  return renderModelToText(buildExportModel(sections, options));
}
